<script>
import { Collapse } from 'vue-collapsed';
import Media from '@/components/elements/Media.vue';

export default {
    name: 'FilterGrid',
    props: {
        configString: {
            type: String,
            required: true
        },
        paginationSizeDefault: {
            type: String,
            default: '24'
        }
    },
    data() {
        return {
            currentFilters: {},
            isFilterDrawerOpen: false,

            searchText: '',

            visibleItemLimit: null, // set in mounted()
            paginationSize:   null, // set in mounted()

            currentItemCount: 0,
            visibleItemCount: 0,

            isArchiveOpen: false
        }
    },
    computed: {
        config () {
            return this.configString ? JSON.parse(this.configString) : null;
        },
        filterOptions () {
            const currentTab = this.currentFilters['tab'] || ['all'];

            let filters = {};

            Object.entries(this.$refs).forEach(([slug, $el]) => {
                if (!$el.filterJson) return;

                Object.entries($el.filterJson).forEach(([key, val]) => {
                    const isItemInCurrentTab = $el.filterJson.tab.some(item => currentTab.includes(item));

                    if (!val) return;
                    
                    // only show filters for items that are in the current tab
                    // and make sure to *not* filter out tab filters
                    if (key === 'tab' || currentTab == 'all' || isItemInCurrentTab) {
                        if (filters[key]) {
                            filters[key] = [...new Set([...val, ...filters[key]])];
                        } else {
                            filters[key] = val;
                        }
                    }
                });
            });

            // alphtabetize
            Object.entries(filters).forEach(([key, val]) => {
                if (key === 'year' || key === 'date') {
                    filters[key] = val.sort().reverse();
                } else {
                    filters[key] = val.sort();
                }
            });

            // add "All" tab if enabled
            if (this.config.tabs.showAll) {
                const allTab = ['all'];
                filters['tab'] = filters['tab'] ? [...allTab, ...filters['tab']] : filters['tab'];
            }

            return filters;
        },
        currentFilterTags () {
            const current = this.currentFilters;

            if (!current) return;

            let tags = [];

            Object.entries(current).forEach(([slug, currentFilters]) => {
                if (slug == 'tab' || currentFilters.length < 1) return;

                currentFilters.forEach(currentFilter => {
                    tags.push({
                        filter: slug,
                        slug: currentFilter,
                        title: this.camelToTitleCase(currentFilter)
                    })
                });
            });

            return tags;
        },
        isHiddenCurrentItems () {
            return this.currentItemCount > this.visibleItemLimit;
        }
    },
    mounted () {
        const { config } = this;

        // save filter parameter json for all items
        Object.entries(this.$refs).forEach(([slug, $el]) => {
            $el.filterJson = JSON.parse($el.dataset.filters);
            $el.matchText = $el.dataset.matchText ?? '';
        });

        // set pagination options
        this.paginationSize = parseInt(this.paginationSizeDefault, 10);
        this.visibleItemLimit = parseInt(this.paginationSizeDefault, 10);

        // set initial tab
        if (config.tabs && config.tabs.initial) {
            this.currentFilters['tab'] = [config.tabs.initial];
        }

        this.filterItems();

    },
    methods: {
        filterItems () {
            const { currentFilters, visibleItemLimit } = this;

            let currentItemCount = 0;
            let visibleItemCount = 0;

            Object.entries(this.$refs).forEach(([slug, $el]) => {
                const filters = $el.filterJson;
                const matchText = $el.matchText;
                const isCurrent = matchText.includes(this.searchText) && Object.entries(filters).every(([key, val]) => {
                    const currentFilter = currentFilters[key];

                    if (!currentFilter || currentFilter == 'all' || currentFilter.length < 1) return true;

                    return val.some(item => currentFilter.includes(item));
                });

                const isVisible = isCurrent && currentItemCount < visibleItemLimit;

                if (isCurrent) currentItemCount++;
                if (isVisible) visibleItemCount++;

                if (isVisible) {
                    $el.style.display = 'block';
                } else {
                    $el.style.display = 'none';
                }
            });

            this.currentItemCount = currentItemCount;
            this.visibleItemCount = visibleItemCount;
        },
        loadNextPage () {
            this.visibleItemLimit = this.visibleItemLimit + this.paginationSize;
            this.filterItems();
        },
        setCurrentTab (slug) {
            this.visibleItemLimit = this.paginationSize;
            this.setCurrentFilters('tab', slug);
        },
        setCurrentFilters (filter, value) {
            const current = this.currentFilters[filter];

            if (current !== [value]) {
                this.currentFilters[filter] = [value];
                this.filterItems();
            }
        },
        addToCurrentFilters (filter, value) {
            const current = this.currentFilters[filter];

            if (!current || value == 'all') {
                this.setCurrentFilters(filter, value);
            } else {
                this.currentFilters[filter].push(value);
                this.filterItems();
            }
        },
        removeFromCurrentFilters (filter, value) {
            const current = this.currentFilters[filter] || [];

            if (current.length > 0) {
                this.currentFilters[filter] = current.filter(function(val){ 
                    return val !== value;
                });
                this.filterItems();
            }
        },
        addOrRemoveFromCurrentFilters (filter, value) {
            const current = this.currentFilters[filter];

            if (current && current.includes(value)) {
                this.removeFromCurrentFilters(filter, value);
            } else {
                this.addToCurrentFilters(filter, value);
            }
        },
        clearAllCurrentFilters () {
            if (this.currentFilters.tab) {
                this.currentFilters = {
                    tab: this.currentFilters.tab
                }
            } else {
                this.currentFilters = {};
            }

            this.filterItems();
        },
        tabLinkClasses (filter, value) {
            const { currentFilters } = this;
            return {
                'state-active': currentFilters[filter] == value
            }
        },
        filterLinkClasses (filter, value) {
            const currentFilter = this.currentFilters && Array.isArray(this.currentFilters[filter]) && this.currentFilters[filter].length > 0 ? this.currentFilters[filter] : null;

            const isActive = currentFilter && currentFilter.includes(value);

            return {
                'state-active': isActive
            }
        },
        archiveTriggerClick () {
            this.isArchiveOpen = !this.isArchiveOpen;
        },
        searchKeyup (e) {
            this.searchText = e.target.value.toLowerCase();
            this.filterItems();
        },
        camelToTitleCase (text) {
            const result = text.replace(/([A-Z])/g, " $1");
            return result.charAt(0).toUpperCase() + result.slice(1);
        },
        slugify (text) {
            return String(text)
                .normalize('NFKD') // split accented chars into base chars + diacritical marks
                .replace(/[\u0300-\u036f]/g, '') // remove accents (all in the \u03xx block)
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
                .replace(/\s+/g, '-') // replace spaces with hyphens
                .replace(/-+/g, '-') // remove consecutive hyphensr
        }
    },
    components: {
        Collapse,
        Media
    }
};
</script>